// Componente autonomo che replica esattamente l'animazione automatica
// del Playground di Palindromo: ogni 1.2s nuovo set di parametri random,
// transizione CSS di 0.6s con easing cubic-bezier(0.22, 1, 0.36, 1).
//
// Versione .jsx del companion RandomXAnimation.tsx (tipi rimossi per CRA
// senza TypeScript). Uso:
//   <RandomXAnimation />                    // default 400px, rosso su nero
//   <RandomXAnimation size={600} color="#fff" bg="transparent" />

import { useEffect, useState } from 'react';

const SIZE = 400;

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

// Costruisce il path SVG composto: quadrato esterno + 4 tacche triangolari.
// fill-rule="evenodd" ritaglia le tacche dal quadrato → forma a X.
function buildPath({ innerSize, offsetX, offsetY, wA, wB, wC, wD }) {
  const maxOff = 170 - innerSize;
  const ox = clamp(offsetX, -maxOff, maxOff);
  const oy = clamp(offsetY, -maxOff, maxOff);
  const cx = 200 + ox;
  const cy = 200 + oy;
  const iL = cx - innerSize;
  const iR = cx + innerSize;
  const iT = cy - innerSize;
  const iB = cy + innerSize;

  const outer = `M 0 0 L ${SIZE} 0 L ${SIZE} ${SIZE} L 0 ${SIZE} Z`;
  const notchA = wA > 0 ? `M ${cx - wA / 2} 0 L ${cx} ${iT} L ${cx + wA / 2} 0 Z` : '';
  const notchB = wB > 0 ? `M ${SIZE} ${cy - wB / 2} L ${iR} ${cy} L ${SIZE} ${cy + wB / 2} Z` : '';
  const notchC = wC > 0 ? `M ${cx + wC / 2} ${SIZE} L ${cx} ${iB} L ${cx - wC / 2} ${SIZE} Z` : '';
  const notchD = wD > 0 ? `M 0 ${cy + wD / 2} L ${iL} ${cy} L 0 ${cy - wD / 2} Z` : '';

  return [outer, notchA, notchB, notchC, notchD].filter(Boolean).join(' ');
}

// Range più ampi del Playground originale per dare più dinamicità anche
// al "centro" della X (innerSize 30-140 invece di 40-94, notch 20-100 invece di 30-89).
const INNER_SIZE_MIN = 30;
const INNER_SIZE_MAX = 140;
const NOTCH_MIN = 20;
const NOTCH_MAX = 100;

function randomShape() {
  const innerSize = INNER_SIZE_MIN + Math.floor(Math.random() * (INNER_SIZE_MAX - INNER_SIZE_MIN + 1));
  const maxOff = Math.max(0, 170 - innerSize);
  const r = () => NOTCH_MIN + Math.floor(Math.random() * (NOTCH_MAX - NOTCH_MIN + 1));
  return {
    innerSize,
    offsetX: (Math.random() * 2 - 1) * maxOff,
    offsetY: (Math.random() * 2 - 1) * maxOff,
    wA: r(),
    wB: r(),
    wC: r(),
    wD: r(),
  };
}

export default function RandomXAnimation({
  size = 400,
  // Se true, l'SVG riempie il container (width/height 100%) ignorando size.
  // Utile quando vuoi che la X si adatti a un layout responsivo.
  fill = false,
  color = '#eb0028',
  bg = '#000000',
  intervalMs = 1200,
  transitionMs = 600,
  className,
}) {
  const [shape, setShape] = useState(() => randomShape());

  useEffect(() => {
    const t = setInterval(() => setShape(randomShape()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);

  const d = buildPath(shape);
  const dim = fill ? { width: '100%', height: '100%' } : { width: size, height: size };

  return (
    <svg
      {...dim}
      viewBox="0 0 400 400"
      preserveAspectRatio="xMidYMid meet"
      className={className}
      style={{ display: 'block', background: bg }}
      role="img"
      aria-label="X animata"
    >
      <path
        d={d}
        fill={color}
        fillRule="evenodd"
        style={{
          transition: `d ${transitionMs}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        }}
      />
    </svg>
  );
}
